import { IconButton } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { GrDocumentDownload } from 'react-icons/gr';
import { toast } from 'react-toastify';

import { TrainingTypeEnum } from '@/constants/trainingTypeOptions';
import api from '@/services/apiSgft';
import { PositionTrainingMatrixFilters } from '@/state/PositionTrainingMatrixFilter.atom';
import { ProcedureTrainingFilters } from '@/state/ProcedureTraining.atom';
import { MesAno } from '@/utils/MesAno';

import LightTooltip from './LightTooltip';
import { MesAnoDialog } from './MesAnoDialog';

interface ExtractTrainingAuditButtonProps {
	filteredData: ProcedureTrainingFilters | PositionTrainingMatrixFilters;
	trainingType: TrainingTypeEnum;
}

export const ExtractTrainingAuditButton: React.FC<
	ExtractTrainingAuditButtonProps
> = (props) => {
	const [mesAno, setMesAno] = useState<MesAno>(
		new MesAno(new Date().getMonth() + 1, new Date().getFullYear()),
	);
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [isEverything, setIsEverything] = useState(false);
	const [isDisabled, setIsDisabled] = useState(true);

	useEffect(() => {
		// Regulatory matrix audit logs can only be extracted if there are filters applied
		if (props.trainingType === TrainingTypeEnum.REGULATORY) {
			for (const key in props.filteredData) {
				if (key !== 'trainingType' && props.filteredData[key]?.length > 0) {
					setIsDisabled(false);
					return;
				}
			}
			setIsDisabled(true);
		} else {
			// Procedure matrix audit logs can be extracted without filters
			setIsDisabled(false);
		}
	}, [props.filteredData, props.trainingType]);

	const handleDownload = async (includeDates: boolean) => {
		try {
			const params: {
				startDate?: string;
				endDate?: string;
				allTime?: boolean;
			} = {};

			if (includeDates) {
				params.startDate = mesAno.start.toISOString();
				params.endDate = mesAno.end.toISOString();
			} else {
				params.allTime = true;
			}

			const path =
				props.trainingType === TrainingTypeEnum.REGULATORY
					? 'regulatory'
					: 'procedure';

			const response = await api.get(`extraction/training-audit-logs/${path}`, {
				params: {
					...params,
					...props.filteredData,
				},
				responseType: 'blob',
			});

			const url = window.URL.createObjectURL(
				new Blob([response.data], {
					type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
				}),
			);

			const downloadFileName = `Histórico de Alterações na Matriz de ${props.trainingType}s.xlsx`;
			const link = document.createElement('a');

			link.href = url;
			link.setAttribute('download', downloadFileName);
			document.body.appendChild(link);

			link.click();
			link.remove();
		} catch (e) {
			toast.error('Erro ao realizar extração', {
				theme: 'colored',
				toastId: 'error',
			});
			throw e;
		} finally {
			setIsDialogOpen(false);
		}
	};

	return (
		<>
			<div className="cursor-pointer">
				<LightTooltip title="Extrair Relatório de Alterações da Matriz">
					<span>
						<IconButton
							onClick={() => setIsDialogOpen(true)}
							disabled={isDisabled}
						>
							<GrDocumentDownload />
						</IconButton>
					</span>
				</LightTooltip>
			</div>
			<MesAnoDialog
				title={`Selecione o mês do Relatório de Alterações da Matriz`}
				mesAno={mesAno}
				setMesAno={setMesAno}
				isEverything={isEverything}
				setIsEverything={setIsEverything}
				isOpen={isDialogOpen}
				setIsOpen={setIsDialogOpen}
				onConfirmation={() => handleDownload(!isEverything)}
			/>
		</>
	);
};
